import React, { FC, useCallback, useEffect, useState, useRef } from 'react'

const ORDER_ROW_HEIGHT = 102
const UNAVAILABLE_SECTION_GAP = 32
import { reject } from 'lodash/fp'
import qs from 'qs'
import { RouteComponentProps } from 'react-router'
import { format } from 'date-fns'
import AutoSizer from 'react-virtualized-auto-sizer'
import { VariableSizeList } from 'react-window'
import useThunkDispatch from '../../../common/useThunkDispatch'
import Modal from '../../../components/Modal'
import Button from '../../../components/Button'
import Splash from '../../../components/Splash'
import Search from '../../../components/Search'
import {
  fetchFlowAccess,
  IDetailsOrder,
  IOrderItem,
  isEditableOrder,
} from '../../../types/orders'
import { orderTotal } from '../../../common/orders'
import withAccountAndBranch from '../../../common/withAccountAndBranch'
import { searchProducts } from '../../../accounts/accountsActions'
import { IAccount, IBranch, IUser } from '../../../types/user'
import { IProduct } from '../../../types/products'
import OrderRow from '../OrderRow'
import OrderProductRow from '../OrderProductRow'
import {
  requestDetailsOrder,
  requestOrderApprove,
  requestOrderReject,
  requestUpdateOrderItem,
  requestCreateOrderItem,
  requestOrderUpdate,
  requestOrderSubmit,
  requestOrderRequest,
} from '../ordersActions'
import OrderHeader from './OrderHeader'
import { useOrderApproveModal } from './OrderApprove'
import { useOrderRejectModal } from './OrderReject'
import { useOrderPONumberModal } from './OrderPONumber'
import { useOrderRequestModal } from './OrderRequest'
import UnavailableProductsBanner from '../UnavailableProductsBanner'
import { useAmountDrafts } from '../../../common/useAmountDrafts'
import { find } from 'lodash'

interface IOrderRouteParams
  extends RouteComponentProps<{
    orderId: string
    accountId?: string
    branchId?: string
    returnUrl?: string
  }> {
  user: IUser
  account?: IAccount
  branch?: IBranch
}

const Order: FC<IOrderRouteParams> = ({
  user,
  account: currentAccount,
  branch,
  match,
  location,
  history,
}) => {
  const dispatch = useThunkDispatch()
  const id = Number(match.params.orderId)
  const [term, setTerm] = useState<string>('')
  const [debouncedTerm, setDebouncedTerm] = useState<string>('')
  const activeRequestId = useRef(0)
  const [order, setOrder] = useState<IDetailsOrder | undefined>(undefined)
  const [foundProducts, setFoundProducts] = useState<IProduct[] | null>(null)
  const [searching, setSearching] = useState<boolean>(false)
  const [rejecting, setRejecting] = useState<boolean>(false)
  const [approving, setApproving] = useState<boolean>(false)
  const [requesting, setRequesting] = useState<boolean>(false)
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [updating, setUpdating] = useState<boolean>(false)
  const [editing, setEditing] = useState<boolean>(false)
  const listRef = useRef<VariableSizeList>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [autoEditIndex, setAutoEditIndex] = useState<number | null>(null)
  const account = currentAccount || user.account
  const flowAccess =
    order && account ? fetchFlowAccess(account, user, order) : {}
  const isEditable = flowAccess.canEdit && (!flowAccess.editOnDemand || editing)
  const nothingFound = foundProducts && !foundProducts.length && !searching
  const orderItems = (
    (order && reject({ amountRequested: 0 }, order.orderItems)) ||
    []
  ).filter((item) => item.product)

  const shouldShowUnavailableBanner = order && isEditableOrder(order)

  const unavailableOrderItems = orderItems.filter(
    (item) => item.product.unavailable,
  )
  const availableOrderItems = orderItems.filter(
    (item) => !item.product.unavailable,
  )

  const sortedOrderItems = shouldShowUnavailableBanner
    ? [
        ...unavailableOrderItems.sort((a, b) =>
          a.product.name.localeCompare(b.product.name),
        ),
        ...availableOrderItems.sort((a, b) =>
          a.product.name.localeCompare(b.product.name),
        ),
      ]
    : orderItems

  const hasUnavailableItems =
    shouldShowUnavailableBanner && unavailableOrderItems.length > 0

  const loading =
    searching || rejecting || approving || requesting || submitting || updating

  // load
  useEffect(() => {
    const perform = async () => {
      setEditing(false)
      setOrder(await dispatch(requestDetailsOrder(id)))
    }

    perform()
  }, [id])

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedTerm(term)
    }, 300)

    return () => clearTimeout(handler)
  }, [term])

  useEffect(() => {
    if (!debouncedTerm || !order || !order.branch) {
      setFoundProducts(null)
      return
    }

    const performSearch = async () => {
      const requestId = ++activeRequestId.current
      setSearching(true)

      const results = await dispatch(
        searchProducts(
          account ? account.id : user.account!.id,
          order.branch.id,
          debouncedTerm,
        ),
      )

      if (requestId !== activeRequestId.current) return

      setFoundProducts(results)

      if (listRef.current) listRef.current.scrollTo(0)
      setSearching(false)
    }

    performSearch().catch((error) => {
      console.error('performSearch error:', error)
    })
  }, [debouncedTerm, id])

  // on back
  const handleBack = useCallback(() => {
    const { returnUrl } = qs.parse(location.search.replace('?', ''))
    if (returnUrl) {
      history.push(returnUrl)
      return
    }

    const link = match.params.accountId
      ? `/clients/${match.params.accountId}/branches/${match.params.branchId}/orders`
      : '/orders'
    history.push(link)
  }, [account, branch])

  // on reject
  const handleReject = useCallback(
    (reason: string) => {
      const perform = async () => {
        setRejecting(true)
        const { result, errors } = await dispatch(
          requestOrderReject(id, reason),
        )
        setRejecting(false)

        if (result) {
          setOrder({ ...order, ...result })
          handleBack()
        } else if (errors) {
          alert(errors)
        }
      }

      perform()
    },
    [id, order, handleBack],
  )

  // on update
  const handleUpdate = useCallback(
    (attrs: Partial<IDetailsOrder>) => {
      const perform = async () => {
        setUpdating(true)
        const { result, errors } = await dispatch(requestOrderUpdate(id, attrs))
        setUpdating(false)

        if (result) {
          setOrder({ ...order, ...result })
        } else if (errors) {
          alert(errors)
        }
      }

      perform()
    },
    [id, order],
  )

  // save an order item amount; used by the draft autosave and by create
  const saveOrderItemAmount = useCallback(
    async (item: IOrderItem, amountRequested: number) => {
      setUpdating(true)
      const res = await dispatch(
        requestUpdateOrderItem(order!, { ...item, amountRequested }),
      )
      setUpdating(false)

      if (res.result) {
        setOrder(res.result)
      } else if (res.errors) {
        alert(res.errors)
      }
      return res
    },
    [order],
  )

  // Set below, once handleOrderItemDelete exists — it needs clearDraft from this hook.
  const removeRef = useRef<(item: IOrderItem) => void>()

  const { drafts, onAmountChange, flushItem, flush, clearDraft } =
    useAmountDrafts(
      (item, amount) => saveOrderItemAmount(item as IOrderItem, amount),
      (item) => {
        if (!confirm('Are you sure you want to remove the item from order?'))
          return
        removeRef.current?.(item as IOrderItem)
      },
    )

  // on order item create
  const handleOrderItemCreate = useCallback(
    (product: IProduct) => {
      const perform = async () => {
        const existingOrderItem = orderItems.find(
          (oi) => oi.product?.id === product.id,
        )

        if (existingOrderItem) {
          saveOrderItemAmount(existingOrderItem, 1)
          return
        }

        setUpdating(true)
        const { result, errors } = await dispatch(
          requestCreateOrderItem(order!, { amountRequested: 1, product }),
        )
        setUpdating(false)

        if (result) {
          setOrder(result)
        } else if (errors) {
          alert(errors)
        }
      }

      perform()
    },
    [order, saveOrderItemAmount],
  )

  // on order item remove
  const handleOrderItemDelete = useCallback(
    (item: IOrderItem) => {
      const perform = async () => {
        clearDraft(item.id)
        setUpdating(true)
        const { result, errors } = await dispatch(
          requestUpdateOrderItem(order!, { ...item, amountRequested: 0 }),
        )
        setUpdating(false)

        if (result) {
          setOrder(result)
        } else if (errors) {
          alert(errors)
        }
      }

      perform()
    },
    [order, clearDraft],
  )

  removeRef.current = handleOrderItemDelete

  const handleFocusNextRow = useCallback(
    (index: number) => {
      const total = foundProducts
        ? foundProducts.length
        : sortedOrderItems.length
      const canEditRow = (i: number) => {
        const item = foundProducts
          ? sortedOrderItems.find((oi) => oi.product.id === foundProducts[i].id)
          : sortedOrderItems[i]
        return isEditable && !!item && !item.product.unavailable
      }

      let nextIndex = index + 1
      while (nextIndex < total && !canEditRow(nextIndex)) {
        nextIndex += 1
      }

      if (nextIndex >= total) {
        searchInputRef.current?.focus()
        return
      }

      listRef.current?.scrollToItem(nextIndex)
      setAutoEditIndex(nextIndex)
    },
    [foundProducts, sortedOrderItems, isEditable],
  )

  // on approve
  const handleApprove = useCallback(() => {
    const perform = async () => {
      setApproving(true)
      if (!(await flush())) {
        setApproving(false)
        return
      }
      const { result, errors } = await dispatch(requestOrderApprove(id))
      setApproving(false)

      if (result) {
        setOrder({ ...order, ...result })
        handleBack()
      } else if (errors) {
        alert(errors)
      }
    }

    perform()
  }, [order, id, handleBack, flush])

  // on request
  const handleRequest = useCallback(() => {
    const perform = async () => {
      setRequesting(true)
      if (!(await flush())) {
        setRequesting(false)
        return
      }
      const { result, errors } = await dispatch(requestOrderRequest(id))
      setRequesting(false)

      if (result) {
        setOrder({ ...order, ...result })
        handleBack()
      } else if (errors) {
        alert(errors)
      }
    }

    perform()
  }, [order, id, handleBack, flush])

  // on order submit
  const handleSubmit = useCallback(() => {
    const perform = async () => {
      setSubmitting(true)
      if (!(await flush())) {
        setSubmitting(false)
        return
      }
      const { result, errors } = await dispatch(requestOrderSubmit(id))
      setSubmitting(false)

      if (result) {
        setOrder({ ...order, ...result })
        handleBack()
      } else if (errors) {
        alert(errors)
      }
    }

    perform()
  }, [order, id, handleBack])

  // approve
  const [{ open: openApproveModal, close: closeApproveModal }, approveModal] =
    useOrderApproveModal({
      onApprove: () => {
        handleApprove()
        closeApproveModal()
      },
    })

  // request
  const [{ open: openRequestModal, close: closeRequestModal }, requestModal] =
    useOrderRequestModal({
      onRequest: () => {
        handleRequest()
        closeRequestModal()
      },
    })

  // reject
  const [{ open: openRejectModal, close: closeRejectModal }, rejectModal] =
    useOrderRejectModal({
      onReject: (reason: string) => {
        handleReject(reason)
        closeRejectModal()
      },
    })

  // po number
  const [
    { open: openPONumberModal, close: closePONumberModal },
    poNumberModal,
  ] = useOrderPONumberModal({
    poNumber: order && order.purchaseOrderNumber,
    onSave: (poNumber: string) => {
      handleUpdate({ purchaseOrderNumber: poNumber })
      closePONumberModal()
    },
  })

  if (!order) return <Splash isLoading />

  const gmFlow = account && account.orderApproveFlow
  const multiCatalogEnabled = (account?.erpCatalogs || []).length > 1
  const showLowStock =
    isEditable &&
    !!orderItems.length &&
    !!find(
      orderItems,
      (oi) =>
        oi.product?.stockInventory !== undefined &&
        oi.amountRequested &&
        oi.amountRequested > oi.product!.stockInventory,
    )

  return (
    <Modal closeable={false} containerClassName="">
      <div className="order-modal">
        <OrderHeader
          order={order}
          onNumberEdit={openPONumberModal}
          onClose={handleBack}
          gmFlow={gmFlow}
          erpOrders={account?.erpOrders}
        />
        <div className="form">
          <div className="order-modal_search">
            <div className="order-modal_field">
              <Search
                ref={searchInputRef}
                searching={searching}
                placeholder="Search by name or ID"
                value={term}
                onChange={(e) =>
                  setTerm(e.target.value ? e.target.value.trim() : '')
                }
                onKeyDown={(e) => {
                  if (e.key === 'Tab' && !e.shiftKey) {
                    e.preventDefault()
                    handleFocusNextRow(-1)
                  }
                }}
              />
            </div>
            <div className="order-modal_total">
              Total
              <br />
              <strong>{orderTotal(order)}</strong>
            </div>
          </div>
          {hasUnavailableItems && (
            <UnavailableProductsBanner
              onGoToPlan={() => history.push('/plan')}
            />
          )}
          {showLowStock && (
            <div className="order-modal_alert">
              Some items could be delayed due to a lack of availability -
              Forshaw CSR will be in touch to discuss
            </div>
          )}
          <div className="order-modal_list">
            {nothingFound && (
              <div className="table">
                <div className="table_head-tr">
                  <div className="table_head-tr table_td table_title">
                    No products found
                  </div>
                </div>
              </div>
            )}
            <AutoSizer
              className="table"
              style={{ height: 'calc(100vh - 265px)' }}
            >
              {(size) => {
                const unavailableCount = shouldShowUnavailableBanner
                  ? unavailableOrderItems.length
                  : 0
                const getItemSize = (index: number) => {
                  if (foundProducts) return ORDER_ROW_HEIGHT
                  const isLastUnavailable =
                    unavailableCount > 0 && index === unavailableCount - 1
                  return isLastUnavailable
                    ? ORDER_ROW_HEIGHT + UNAVAILABLE_SECTION_GAP
                    : ORDER_ROW_HEIGHT
                }
                return (
                  <VariableSizeList
                    ref={listRef}
                    className="wonder-scroll"
                    {...size}
                    itemData={{
                      editable: isEditable,
                      products: foundProducts,
                      onAdd: handleOrderItemCreate,
                      drafts,
                      onAmountChange,
                      onAmountFlush: flushItem,
                      onDelete: handleOrderItemDelete,
                      onFocusNextRow: handleFocusNextRow,
                      onFocusRow: setAutoEditIndex,
                      autoEditIndex,
                      onAutoEditHandled: () => setAutoEditIndex(null),
                      orderItems: sortedOrderItems,
                      unavailableCount,
                      multiCatalogEnabled,
                      loading,
                    }}
                    itemCount={
                      foundProducts
                        ? foundProducts.length
                        : sortedOrderItems.length
                    }
                    itemSize={getItemSize}
                    itemKey={(index) =>
                      foundProducts
                        ? foundProducts[index].id
                        : sortedOrderItems[index].id
                    }
                  >
                    {foundProducts ? OrderProductRow : OrderRow}
                  </VariableSizeList>
                )
              }}
            </AutoSizer>
          </div>
        </div>
        <div className="form_controls">
          <div className="order-modal_footer form_controls-container">
            {(order.status === 'open' || order.status === 'rejected') && (
              <div className="order-modal_footer_update_info">
                {(() => {
                  const updatedAt = order.updatedAt
                    ? format(new Date(order.updatedAt), 'MMM DD, YYYY')
                    : 'n/a'

                  const name =
                    order!.updatedBy?.name === 'System user'
                      ? 'ForeSight'
                      : order!.updatedBy?.name || 'n/a'

                  return `Updated on ${updatedAt} by ${name}`
                })()}
              </div>
            )}
            {order.status === 'submitted' && (
              <div className="order-modal_footer_update_info">
                {(() => {
                  const submittedAt = order.submittedAt
                    ? format(new Date(order.submittedAt), 'MMM DD, YYYY')
                    : 'n/a'

                  const name =
                    order!.submittedBy?.name === 'System user'
                      ? 'ForeSight'
                      : order!.submittedBy?.name || 'n/a'

                  return `Submitted on ${submittedAt} by ${name}`
                })()}
              </div>
            )}
            {order.status === 'requested' && (
              <div className="order-modal_footer_update_info">
                {(() => {
                  const requestedAt = order.requestedAt
                    ? format(new Date(order.requestedAt), 'MMM DD, YYYY')
                    : 'n/a'

                  const name =
                    order!.requestedBy?.name === 'System user'
                      ? 'ForeSight'
                      : order!.requestedBy?.name || 'n/a'

                  return `Requested on ${requestedAt} by ${name}`
                })()}
              </div>
            )}
            {order.status === 'approved' && (
              <div className="order-modal_footer_update_info">
                {(() => {
                  const approvedAt = order.approvedAt
                    ? format(new Date(order.approvedAt), 'MMM DD, YYYY')
                    : 'n/a'

                  const name =
                    order!.approvedBy?.name === 'System user'
                      ? 'ForeSight'
                      : order!.approvedBy?.name || 'n/a'

                  return `Approved on ${approvedAt} by ${name}`
                })()}
              </div>
            )}

            {flowAccess.canEdit && flowAccess.editOnDemand && !editing && (
              <Button
                type="button"
                color="primary"
                className="form_control"
                onClick={() => setEditing(true)}
              >
                Change order
              </Button>
            )}
            {flowAccess.canReject && (
              <Button
                type="button"
                color="danger"
                disabled={rejecting}
                loading={rejecting}
                className="form_control -size"
                onClick={openRejectModal}
              >
                Reject
              </Button>
            )}
            {flowAccess.canApprove && (
              <Button
                type="button"
                color="success"
                disabled={approving || hasUnavailableItems}
                className="form_control"
                onClick={openApproveModal}
              >
                {flowAccess.approveText || 'Approve'}
              </Button>
            )}
            {flowAccess.canRequest && (
              <Button
                type="button"
                color="success"
                disabled={requesting || hasUnavailableItems}
                className="form_control"
                onClick={openRequestModal}
              >
                Request approval
              </Button>
            )}
            {flowAccess.canSubmit && (
              <Button
                type="button"
                color="success"
                disabled={submitting || hasUnavailableItems}
                className="form_control"
                onClick={handleSubmit}
              >
                Place order
              </Button>
            )}
          </div>
        </div>
      </div>
      {approveModal}
      {rejectModal}
      {poNumberModal}
      {requestModal}
    </Modal>
  )
}

export default withAccountAndBranch(Order)
